import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { notification, Select } from "antd"; // Import Select from AntD
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import api from "../../utils/api";
import "nepali-datepicker-reactjs/dist/index.css";
import { addBooking } from "../../redux/bookingSlice";
import { settingsActions } from "../../redux/settingsSlice"; // Action to handle bookings

const { Option } = Select;

const AddBooking = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Form states
  const [hallName, setHallName] = useState(null);
  const [bookingDate, setBookingDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [bookLoading, setBookLoading] = useState(false);
  const [houseId, setHouseId] = useState("");
  const [houses, setHouses] = useState([]);

  // Options for hall selection
  const options = [
    { label: "Hall A", value: "hall_a", available: true },
    { label: "Hall B", value: "hall_b", available: false },
    { label: "Hall C", value: "hall_c", available: true },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!hallName) newErrors.hallName = "Please select a hall.";
    if (!bookingDate) newErrors.bookingDate = "Booking date is required.";
    if (!startTime) newErrors.startTime = "Start time is required.";
    if (!endTime) newErrors.endTime = "End time is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setBookLoading(true);
    const bookingData = {
      tenant_id: houseId,
      hall_name: hallName.value,
      booking_date: bookingDate,
      start_time: startTime,
      end_time: endTime,
      remarks,
    };

    dispatch(addBooking(bookingData))
      .unwrap()
      .then(() => {
        notification.success({
          message: "Success",
          description: "Booking added successfully",
        });
        setBookLoading(false);

        // Reset form
        setHallName(null);
        setBookingDate("");
        setStartTime("");
        setEndTime("");
        setRemarks("");

        navigate("/dashboard/booking"); // Redirect after success
      })
      .catch((err) => {
        console.log(err);
        notification.error({
          message: "Error",
          description: String(err.error) || "Failed to add booking",
        });
        setBookLoading(false);
      });
  };

  const formatOptionLabel = ({ label, available }) => (
    <div style={{ color: available ? "black" : "red" }}>
      {label} {!available && "(Unavailable)"}
    </div>
  );

  useEffect(() => {
    dispatch(settingsActions.getSettings());
    api
      .get("/getTenant")
      .then((response) => {
        setHouses(response.data);
      })
      .catch((error) => {
        console.error("Error fetching houses:", error);
      });
  }, [dispatch]);

  const handleHouseChange = (e) => {
    const selectedHouseId = e.target.value;
    setHouseId(selectedHouseId);
  };

  return (
    <div className="pb-20 mx-6">
      <h3 className="font-bold flex text-[22px]">Add Booking</h3>
      <div className="bg-gray-300 rounded-lg p-4">
        <div className="py-6 bg-white rounded-lg m-3 p-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 w-full">
              <label>House ID:</label>
              <select
                className="rounded-md py-2 px-4 border-[2px] border-gray-400"
                value={houseId}
                onChange={handleHouseChange}
                required
              >
                <option value="">Select House Id</option>
                {houses?.data?.map((house) => (
                  <option key={house.house_number} value={house.id}>
                    {house.house_number}({house.user_name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="hall_name" className="font-bold text-md">
                Choose Your Hall
              </label>
              <Select
                id="hall_name"
                name="hall_name"
                options={options.map((option) => ({
                  label: option.label,
                  value: option.value,
                  disabled: !option.available,
                }))}
                value={hallName}
                onChange={(value, option) =>
                  setHallName({ value, available: option.disabled !== true })
                }
                className="mt-2 w-full"
                placeholder="Select Hall"
              />
              {errors.hallName && (
                <p className="text-red-500 text-sm">{errors.hallName}</p>
              )}
            </div>

            <div>
              <label htmlFor="booking_date" className="font-bold text-md">
                Booking Date
              </label>
              <NepaliDatePicker
                id="booking_date"
                name="booking_date"
                className="w-full custom-date-picker mt-2"
                options={{ calenderLocale: "en", valueLocale: "en" }}
                value={bookingDate}
                onChange={setBookingDate}
                required
              />
              {errors.bookingDate && (
                <p className="text-red-500 text-sm">{errors.bookingDate}</p>
              )}
            </div>

            <div>
              <label htmlFor="start_time" className="font-bold text-md">
                Start Time
              </label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
              {errors.startTime && (
                <p className="text-red-500 text-sm">{errors.startTime}</p>
              )}
            </div>

            <div>
              <label htmlFor="end_time" className="font-bold text-md">
                End Time
              </label>
              <input
                type="time"
                id="end_time"
                name="end_time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
              {errors.endTime && (
                <p className="text-red-500 text-sm">{errors.endTime}</p>
              )}
            </div>

            <div>
              <label htmlFor="remarks" className="font-bold text-md">
                Remarks
              </label>
              <input
                type="text"
                id="remarks"
                name="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
            </div>

            <button
              type="submit"
              className={`bg-[#403F93] text-white flex px-16 py-3 rounded-lg mt-6 ${
                bookLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={bookLoading}
            >
              {bookLoading ? "Loading..." : "Book"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddBooking;
